import React, {useEffect, useState} from 'react';
import {Button, Checkbox, DatePicker, Input, message, Modal, Select, Space, Table, Tooltip} from 'antd';
import {EditOutlined, FileAddOutlined, FileOutlined, SaveOutlined, UndoOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import axios from 'axios';
import {getConfig} from "../../../config";
import {useProjectContext} from "../../../context/ProjectContext";
import {useDirtyContext} from "../../../context/DirtyContext";
import {ViewType} from "../../../types/ViewType";
import "./OrderCardConfiguration.css";
import locale from "antd/es/date-picker/locale/de_DE";
import dayjs from "dayjs";
import "dayjs/locale/de";
import updateLocale from "dayjs/plugin/updateLocale";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";

dayjs.extend(updateLocale);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

// 1) globale Locale setzen (AntD-`locale` reicht NICHT für den Wochenstart)
dayjs.locale('de');

// 2) sicherheitshalber auf Montag setzen
dayjs.updateLocale('de', { weekStart: 1 });

const { Option } = Select;
const { API_BASE_URL } = getConfig();

// --- NEW: fehlende Typen ergänzt (keine Änderungen an bestehenden Namen/Props) ---
interface OGLCode {
    ogl: string;
    description: string;
}

interface JobCode {
    alias: string;
    descriptionSpez: string;
}

interface OrderCardConfigurationProps {
    selectedProject?: string;
    jobCodes?: JobCode[];
    viewType?: ViewType;
    oglCodes?: OGLCode[];
    orderCardData?: OrderCardData[];
    setOrderCardData?: (data: OrderCardData[]) => void;
}

const OrderCardConfiguration: React.FC<OrderCardConfigurationProps> = ({
                                                                           selectedProject,
                                                                           jobCodes = [],
                                                                           viewType,
                                                                           oglCodes = [],
                                                                           orderCardData = [],
                                                                           setOrderCardData,
                                                                       }) => {
    const [data, setData] = useState<OrderCardData[]>(orderCardData.length > 0 ? orderCardData : []);
    const [originalData, setOriginalData] = useState<OrderCardData[]>(orderCardData.length > 0 ? orderCardData : []);
    const [localJobCodes, setLocalJobCodes] = useState<JobCode[]>(jobCodes.length > 0 ? jobCodes : []);
    const [localOGLCodes, setLocalOGLCodes] = useState<OGLCode[]>(oglCodes.length > 0 ? oglCodes : []);
    const [reasonSelectResetKey, setReasonSelectResetKey] = useState(0);
    const { selectedProject: contextProject } = useProjectContext();
    const [isLoading, setIsLoading] = useState(false);
    const { isDirty, setDirty } = useDirtyContext();
    const activeProject = selectedProject || contextProject;
    const [hasFetched, setHasFetched] = useState(false);
    const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
    const [isDocumentUploadModalVisible, setIsDocumentUploadModalVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<OrderCardData | null>(null);
    const [supplements, setSupplements] = useState<string[]>([]);
    const [isSavingNewCard, setIsSavingNewCard] = useState(false);
    const [isSystemPlanSaving, setIsSystemPlanSaving] = useState(false);
    const [heliosSystemPlansUrl, setHeliosSystemPlansUrl] = useState<string>('');
    const [newOrderCard, setNewOrderCard] = useState<Partial<OrderCardData>>({
        supplement: '',
        protocol: '',
        description: '',
        estimatedValue: '',
        status: '',
        jobCodeId: '',
        oglCodeId: '',
        mountingDate: undefined,
        insideOutside: '',
        relevantDeadline: '',
        assemblyType: '',
        defectCardReason: null,
        defectCard: false,
        regieCard: false,
        supplierType: '',
        leapTime: 1,
    });
    const [isDescriptionEdited, setIsDescriptionEdited] = useState(false);
    const generateDescription = (oglCodeId: string, jobCodeId: string) => {
        const ogl = localOGLCodes.find(o => o.ogl === oglCodeId)?.description || "";
        const job = localJobCodes.find(j => j.alias === jobCodeId)?.descriptionSpez || "";
        return `${ogl} - ${job}`.trim();
    };
    // --- NEW: Vorlagen + Puffer für zuletzt genutzte Begründungen ---
    const TEMPLATES: string[] = [
        "Baustelle nicht parat ",
        "Keine Ware ",
        "Gemäss E-Mail von ",
        "Gemäss Telefon mit ",
    ];

    const [recentDelayReasons, setRecentDelayReasons] = useState<string[]>([]);
    const rememberReason = (reason: string) => {
        const r = (reason ?? "").trim();
        if (!r) return;
        setRecentDelayReasons(prev => {
            const next = [r, ...prev.filter(x => x !== r)];
            return next.slice(0, 8); // z. B. max. 8 Einträge puffern
        });
    };

    const [isDateChangeModalVisible, setIsDateChangeModalVisible] = useState(false); // NEW
    const [pendingDateChange, setPendingDateChange] = useState<{ recordId: number; newWeek: dayjs.Dayjs | null } | null>(null); // NEW
    const [pendingDelayReason, setPendingDelayReason] = useState<string>(''); // NEW
    const { TextArea } = Input; // NEW

    const defectReasons = [
        { code: "MEASURING_OPEN", label: "Das Messen wurde noch nicht beauftragt" },   // Beauftragung Messen
        { code: "CONSTRUCTION_OPEN", label: "Die Konstruktion wurde noch nicht beauftragt" },  // Beauftragung Konstruktion
        { code: "PRODUCTION_OPEN", label: "Die Produktion wurden noch nicht beauftragt" },  // Bestellung Produktion
        { code: "MOUNTING_OPEN", label: "Die Montage wurde noch nicht geplant" }, // Montage
    ];

    useEffect(() => {
        const fetchSupplements = async () => {
            try {
                const response = await axios.get<string[]>(
                    `${API_BASE_URL}/getAllSupplementsOfProject?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${activeProject}`
                );
                setSupplements(response.data);
            } catch (error) {
                console.error("Error fetching supplements:", error);
            }
        };
        if (isModalVisible) {
            fetchSupplements();
        }
    }, [isModalVisible]);

    const defaultLeapTime = {
        Gukom: 35,
        Werkstatt: 10,
        Dritte: 3,
    };

    const fetchData = async (projectNo: string) => {
        setIsLoading(true);
        try {
            const response = await axios.get<OrderCardData[]>(
                `${API_BASE_URL}/getNetworkPlanOrderCardData?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${activeProject}`
            );
            setData(response.data.map((item, index) => ({...item, key: index})));
            setOriginalData(response.data.map((item, index) => ({ ...item, key: index })));
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Fehler beim Laden der Daten.');
        } finally {
            setIsLoading(false);
        }
    };

    const [filters, setFilters] = useState({
        jobCodeId: '',
        oglCodeId: '',
        insideOutside: '',
        relevantDeadline: '',
        description: '',
        supplement: '',
        protocol: '',
        assemblyType: '',
        supplierType: '',
        mountingDate: '',
    });

    const asIso = (d: dayjs.Dayjs) => d as unknown as {
        isoWeek(): number;
        isoWeek(week: number): dayjs.Dayjs;
        isoWeekYear(): number;
        isoWeekYear(year: number): dayjs.Dayjs;
        startOf: (unit: string) => dayjs.Dayjs;
    };

    const weekStringToDayjs = (wk?: string) => {
        if (!wk) return undefined;
        const [y, w] = wk.split("/").map(n => parseInt(n, 10));
        if (Number.isNaN(y) || Number.isNaN(w) || w < 1) return undefined;

        // 4. Januar ist immer in ISO-KW 1 => von dort (w-1) Wochen vorwärts
        const jan4 = dayjs(`${y}-01-04`);
        return jan4.startOf('isoWeek').add(w - 1, 'week');
    };

    const dayjsToWeekString = (d: dayjs.Dayjs) => {
        const s = d.startOf('isoWeek');
        return `${s.isoWeekYear()}/${String(s.isoWeek()).padStart(2, "0")}`;
    };

    const disablePastWeeks = (current: dayjs.Dayjs) =>
        current && current.endOf("isoWeek").isBefore(dayjs().startOf("day"));

    const openDateChangeModal = (value: dayjs.Dayjs | null, record: OrderCardData) => {
        setPendingDateChange({ recordId: record.id, newWeek: value || null });
        setPendingDelayReason('');           // Textarea leeren
        setReasonSelectResetKey(k => k + 1); // einmalig beim Modal-Open remounten
        setIsDateChangeModalVisible(true);
    };

    const confirmDateChange = () => {
        if (!pendingDateChange) return;
        const reason = (pendingDelayReason ?? '').trim();
        if (!reason) return;
        rememberReason(reason);
        const wk = pendingDateChange.newWeek ? dayjsToWeekString(pendingDateChange.newWeek) : '';
        const updatedData = data.map(item =>
            item.id === pendingDateChange.recordId
                ? { ...item, mountingDate: wk, delayReason: reason }
                : item
        );
        setData(updatedData);
        setOrderCardData?.(updatedData);
        setDirty(true);
        setIsDateChangeModalVisible(false);
        setPendingDateChange(null);
        setPendingDelayReason('');
    };


    const cancelDateChange = () => {
        setIsDateChangeModalVisible(false);
        setPendingDateChange(null);
        setPendingDelayReason('');
    };

    const handleFilterChange = (value: string | undefined, field: keyof typeof filters) => {
        let actualValue = value || '';
        if (field === 'oglCodeId') {
            console.log("👀 Selected description:", value);
            const matchingCode = localOGLCodes.find(code => code.ogl === value);
            if (matchingCode) {
                actualValue = matchingCode.description;
                console.log("✅ Matching oglCode found:", matchingCode);
            } else {
                console.warn("❌ No matching oglCode found for description:", value);
                actualValue = '';
            }
        }
        const updated = { ...filters, [field]: actualValue };
        console.log("📦 Updated filters:", updated);
        setFilters(updated);
        const filtered = originalData.filter(item => {
            const match =
                (!updated.jobCodeId || item.jobCodeId === updated.jobCodeId) &&
                (!updated.oglCodeId || String(item.oglCodeId) === updated.oglCodeId) &&
                (!updated.insideOutside || item.insideOutside === updated.insideOutside) &&
                (!updated.relevantDeadline || item.relevantDeadline === updated.relevantDeadline) &&
                (!updated.assemblyType || item.assemblyType === updated.assemblyType) &&
                (!updated.supplierType || item.supplierType === updated.supplierType) &&
                (!updated.description || (item.description ?? '').toLowerCase().includes(updated.description.toLowerCase())) &&
                (!updated.supplement || item.supplement === updated.supplement) &&
                (!updated.mountingDate || (item.mountingDate ?? '').includes(updated.mountingDate)) &&
                (!updated.protocol || item.protocol === updated.protocol);

            if (updated.oglCodeId) {
                console.log(`🔎 Check oglCodeId: item=${item.oglCodeId} vs filter=${updated.oglCodeId} => match=${String(item.oglCodeId) === updated.oglCodeId}`);
            }
            return match;
        });

        console.log("🧮 Filtered result count:", filtered.length);
        setData(filtered);
    };

    const fetchJobCodes = async (projectNo: string) => {
        try {
            const response = await axios.get<JobCode[]>(
                `${API_BASE_URL}/getNetworkPlanJobCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${activeProject}`
            );
            setLocalJobCodes(response.data);
        } catch (error) {
            console.error('Error fetching job codes:', error);
            message.error('Fehler beim Laden der JobCodes.');
        }
    };

    const fetchOGLCodes = async (projectNo: string) => {
        try {
            const response = await axios.get<OGLCode[]>(
                `${API_BASE_URL}/getNetworkPlanOGLCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${activeProject}`
            );
            setLocalOGLCodes(response.data);
        } catch (error) {
            console.error('Error fetching OGL codes:', error);
            message.error('Fehler beim Laden der OGLCodes.');
        }
    };

    const requiredFieldsFilled = Boolean(
        newOrderCard.supplement &&
        newOrderCard.description &&
        newOrderCard.jobCodeId &&
        newOrderCard.oglCodeId &&
        newOrderCard.defectCardReason &&
        newOrderCard.insideOutside &&
        newOrderCard.relevantDeadline &&
        newOrderCard.assemblyType &&
        newOrderCard.supplierType
    );

    useEffect(() => {
        if (!selectedProject && contextProject) {
            fetchData(contextProject);
            fetchJobCodes(contextProject);
            fetchOGLCodes(contextProject);
        }
    }, [contextProject, selectedProject]);

    const handleDropdownChange = (value: string, key: keyof OrderCardData, recordKey: number) => {
        setDirty(true);
        const updatedData = data.map(item =>
            item.id === recordKey
                ? {
                    ...item,
                    [key]: value,
                    ...(key === 'supplierType' && { leapTime: defaultLeapTime[value as keyof typeof defaultLeapTime] }),
                }
                : item
        );

        setData(updatedData);
        if (setOrderCardData) {
            setOrderCardData(updatedData);
        }
    };

    const handleLeapTimeChange = (value: string, recordKey: number) => {
        setDirty(true);
        const updatedData = data.map(item =>
            item.id === recordKey ? { ...item, leapTime: Number(value) } : item
        );

        setData(updatedData);
        if (setOrderCardData) {
            setOrderCardData(updatedData);
        }
    };

    const handleCheckboxChange = (
        checked: boolean,
        record: OrderCardData,
        field: keyof OrderCardData
    ) => {
        setDirty(true);
        const valueToWrite = checked;
        const updatedData = data.map(item =>
            item.id === record.id ? { ...item, [field]: valueToWrite } : item
        );
        setData(updatedData);
        if (setOrderCardData) {
            setOrderCardData(updatedData);
        }
    };

    const renderCell = (
        value: any,
        record: OrderCardData,
        key: keyof OrderCardData,
        renderInput: (value: any) => React.ReactNode
    ) => (
        <div
            style={{
                padding: '4px',
                borderColor: isCellChanged(record, key) ? 'rgb(159 206 116 / 66%)' : 'transparent',
                borderRadius: isCellChanged(record, key) ? '7px' : '0px',
                borderStyle: isCellChanged(record, key) ? 'groove' : 'none',
            }}
        >
            {renderInput(value)}
        </div>
    );

    const isCellChanged = (record: OrderCardData, key: keyof OrderCardData): boolean => {
        const original = originalData.find(item => item.id === record.id);
        return original ? original[key] !== record[key] : false;
    };

    // --- CHANGED: hasChanges jetzt ID-basiert (robust gegen Filter/Sortierung) ---
    const hasChanges = (): boolean => {
        return data.some((record) => {
            const original = originalData.find(o => o.id === record.id);
            if (!original) return false;
            return (Object.keys(record) as (keyof OrderCardData)[])
                .some((key) => record[key] !== original[key]);
        });
    };

    const handleReset = async () => {
        setIsLoading(true);
        setDirty(false);
        try {
            if (activeProject)  {
                fetchJobCodes(activeProject);
                fetchOGLCodes(activeProject);
            }
            const response = await axios.get<OrderCardData[]>(
                `${API_BASE_URL}/getNetworkPlanOrderCardData?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${activeProject}`
            );
            const orderCards = response.data;
            if (setOrderCardData) {
                setOrderCardData(orderCards);
            }
            setData([...orderCards]);
            message.success('Auftragskarten wurden erfolgreich neu geladen.');
        } catch (error) {
            message.error("Fehler beim Zurücksetzen der Auftragskarten.");
        } finally {
            setIsLoading(false);
        }
    };

    const openDocumentModal = (record: OrderCardData) => {
        setSelectedRecord(record);
        setIsDocumentModalVisible(true);
    };

    const closeDocumentModal = () => {
        setSelectedRecord(null);
        setIsDocumentModalVisible(false);
    };

    const openDocumentUploadModal = (record: any) => {
        setSelectedRecord(record);
        setIsDocumentUploadModalVisible(true);
    };

    const closeDocumentUploadModal = () => {
        setSelectedRecord(null);
        setIsDocumentUploadModalVisible(false);
    };

    const handleDocumentUploadModalClose = () => {
        setHeliosSystemPlansUrl('');
        setIsDocumentUploadModalVisible(false);
    };

    const handleHeliosLinkInputChange = (value: string, setValue: (value: (((prevState: string) => string) | string)) => void) => {
        if (value === "h") {
            setValue("helios://");
        } else if (value.startsWith("helios://") || validateHeliosUrl(value)) {
            setValue(value);
        } else  {
            message.error("Bitte gebe eine gültige Helios-URL an");
        }
    };

    const validateHeliosUrl = (value: string): boolean => {
        return value.startsWith("helios://") && value.length > "helios://".length;
    };

    const handleDocumentUploadModalOk = async () => {
        if (!heliosSystemPlansUrl) return;
        setIsSystemPlanSaving(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/bookSignedSystemplanNetworkOrderByOrderCard`,
                {
                    params: {
                        auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                        heliosURL: heliosSystemPlansUrl,
                        projectNo: activeProject,
                        orderCardId: selectedRecord!!.id,
                    },
                }
            );
            if (response.data) {
                message.success('Daten erfolgreich gebucht!');
                setHeliosSystemPlansUrl('');
                setIsDocumentUploadModalVisible(false);
                if (selectedProject) {
                    fetchData(selectedProject);
                }
            }
        } catch (error) {
            console.error('Fehler:', error);
            message.error('Fehler beim Schreiben');
        } finally {
            setIsSystemPlanSaving(false);
        }
    };

    const handleSaveNewOrderCard = async () => {
        if (!newOrderCard.description) {
            message.error("Bitte Beschreibung ausfüllen.");
            return;
        }
        try {
            setIsSavingNewCard(true);
            await axios.post(`${API_BASE_URL}/createNewOrderCardForNetworkPlan`, {
                projectNo: activeProject,
                orderCardData: [{
                    ...newOrderCard,
                    defectCardReason: newOrderCard.defectCardReason
                }],
            });
            setIsModalVisible(false);
            if (activeProject)  {
                await fetchData(activeProject);
            }
        } catch (error) {
            message.error("Fehler beim Erstellen. Eventuell gibt es keine freie Protokoll-Nummern mehr");
        } finally {
            setIsSavingNewCard(false);
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        setDirty(false);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/saveChangedNetworkPlanOrderCardData`,
                { projectNo: activeProject, data }
            );
            if (response.status === 200) {
                setOriginalData([...data]); // Update original data after successful save
                message.success('Daten erfolgreich gespeichert.');
            } else {
                throw new Error('Speichern fehlgeschlagen.');
            }
        } catch (error) {
            console.error('Error saving data:', error);
            message.error('Fehler beim Speichern der Daten.');
        } finally {
            setIsLoading(false);
        }
    };

    const DLZInputCell = ({ value, record }: {
        value: number;
        record: OrderCardData;
    }) => {
        const [inputValue, setInputValue] = useState(value?.toString() || "");

        useEffect(() => {
            setInputValue(value?.toString() || "");
        }, [value]);

        return (
            <Input
                type="number"
                value={inputValue}
                disabled={record.supplierType !== 'Dritte'}
                onChange={e => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                        setInputValue(val);
                    }
                }}
                onBlur={() => {
                    const num = parseInt(inputValue || "", 10);
                    if (!isNaN(num) && num >= 1) {
                        handleLeapTimeChange(num.toString(), record.id);
                    } else {
                        setInputValue(value?.toString() || "");
                    }
                }}
                style={{
                    borderColor: isCellChanged(record, 'leapTime') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                    borderRadius: isCellChanged(record, 'leapTime') ? '7px' : '0px',
                    borderStyle: isCellChanged(record, 'leapTime') ? 'groove' : 'none',
                    padding: '4px',
                }}
            />
        );
    };

    const columns: ColumnsType<OrderCardData> = [
        {
            title: (
                <div>
                    NT
                    <Input
                        allowClear
                        placeholder="Filter NT"
                        onChange={e => handleFilterChange(e.target.value, 'supplement')}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'supplement',
            key: 'supplement',
            width: 60,
            sorter: (a, b) => a.supplement.localeCompare(b.supplement)
        },
        {
            title: (
                <div>
                    Prot
                    <Input
                        allowClear
                        placeholder="Filter Prot"
                        onChange={e => handleFilterChange(e.target.value, 'protocol')}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'protocol',
            key: 'protocol',
            width: 60
        },
        {
            title: (
                <div>
                    Beschreibung
                    <Input
                        allowClear
                        placeholder="Filter Beschreibung"
                        onChange={e => handleFilterChange(e.target.value, 'description')}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                </div>
            ),
            dataIndex: 'description',
            key: 'description',
            width: 300
        },
        {
            title: 'SW (%)',
            dataIndex: 'estimatedValue',
            className: 'align-top',
            width: 70,
            key: 'estimatedValue'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            className: 'align-top',
            key: 'status',
            render: (value, record) => (
                <Input
                    type="text"
                    value={record.status}
                    disabled={true}
                    style={{
                        borderColor: isCellChanged(record, 'leapTime') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                        borderRadius: isCellChanged(record, 'leapTime') ? '7px' : '0px',
                        borderStyle: isCellChanged(record, 'leapTime') ? 'groove' : 'none',
                        padding: '4px',
                    }}
                />
            ),
            width: 90,
        },
        {
            title: (
                <div>
                    Produkt
                    <Select
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onClick={e => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={val => handleFilterChange(val, 'jobCodeId')}
                        style={{ width: '100%' }}
                    >
                        {localJobCodes.map(code => (
                            <Option key={code.alias} value={code.alias}>
                                {code.descriptionSpez}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'jobCodeId',
            key: 'jobCodeId',
            width: 200,
            render: (value, record) => {
                const selectedJobCode = localJobCodes.find(code => code.alias === value);
                const tooltipTitle = selectedJobCode
                    ? `${selectedJobCode.alias} - ${selectedJobCode.descriptionSpez}`
                    : value;
                return (
                    <Tooltip title={tooltipTitle}>
                        <Select
                            value={value}
                            showSearch
                            allowClear
                            filterOption={(input, option) =>
                                (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                            }
                            onChange={val => handleDropdownChange(val, 'jobCodeId', record.id)}
                            style={{
                                borderColor: isCellChanged(record, 'jobCodeId') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                                borderRadius: isCellChanged(record, 'jobCodeId') ? '7px' : '0px',
                                borderStyle: isCellChanged(record, 'jobCodeId') ? 'groove' : 'none',
                                padding: '4px',
                            }}
                        >
                            {localJobCodes.map(code => (
                                <Option key={code.alias} value={code.alias} title={code.alias}>
                                    {code.descriptionSpez}
                                </Option>
                            ))}
                        </Select>
                    </Tooltip>
                );
            }
        },
        {
            title: (
                <div>
                    Bauabschnitt
                    <Select
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onClick={e => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={val => handleFilterChange(val, 'oglCodeId')}
                        style={{ width: '100%' }}
                    >
                        {localOGLCodes.map(code => (
                            <Option key={code.ogl} value={code.ogl}>
                                {code.description}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'oglCodeId',
            key: 'oglCodeId',
            render: (value, record) => (
                <Select
                    value={value}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    onChange={val => handleDropdownChange(val, 'oglCodeId', record.id)}
                    style={{
                        borderColor: isCellChanged(record, 'oglCodeId') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                        borderRadius: isCellChanged(record, 'oglCodeId') ? '7px' : '0px',
                        borderStyle: isCellChanged(record, 'oglCodeId') ? 'groove' : 'none',
                        padding: '4px',
                    }}
                >
                    {localOGLCodes.map(code => (
                        <Option key={code.ogl} value={code.ogl}>
                            {code.description}
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: (
                <div>
                    Einbauort
                    <Select
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onClick={e => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={val => handleFilterChange(val, 'insideOutside')}
                        style={{ width: '100%' }}
                    >
                        <Option value="Aussen">Aussen</Option>
                        <Option value="Innen">Innen</Option>
                        <Option value="Umgebung">Umgebung</Option>
                        <Option value="Nicht Relevant">Nicht Relevant</Option>
                    </Select>
                </div>
            ),
            dataIndex: 'insideOutside',
            key: 'insideOutside',
            render: (value, record) => (
                <Select
                    value={value}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    onChange={val => handleDropdownChange(val, 'insideOutside', record.id)}
                    style={{
                        borderColor: isCellChanged(record, 'insideOutside') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                        borderRadius: isCellChanged(record, 'insideOutside') ? '7px' : '0px',
                        borderStyle: isCellChanged(record, 'insideOutside') ? 'groove' : 'none',
                        padding: '4px',
                    }}
                >
                    <Option value="Aussen">Aussen</Option>
                    <Option value="Innen">Innen</Option>
                    <Option value="Umgebung">Umgebung</Option>
                    <Option value="Nicht Relevant">Nicht Relevant</Option>
                </Select>
            ),
        },
        {
            title: (
                <div>
                    Relevante Frist
                    <Select
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onClick={e => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={val => handleFilterChange(val, 'relevantDeadline')}
                        style={{ width: '100%' }}
                    >
                        <Option value="Bezugstermin">Bezugstermin</Option>
                        <Option value="Geruesttermin">Gerüsttermin</Option>
                        <Option value="Vormontage (A)">Vormontage (A)</Option>
                        <Option value="Vormontage (I)">Vormontage (I)</Option>
                        <Option value="Nicht Relevant">Nicht Relevant</Option>
                    </Select>
                </div>
            ),
            dataIndex: 'relevantDeadline',
            key: 'relevantDeadline',
            render: (value, record) => (
                <Select
                    value={value}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    onChange={val => handleDropdownChange(val, 'relevantDeadline', record.id)}
                    style={{
                        borderColor: isCellChanged(record, 'relevantDeadline') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                        borderRadius: isCellChanged(record, 'relevantDeadline') ? '7px' : '0px',
                        borderStyle: isCellChanged(record, 'relevantDeadline') ? 'groove' : 'none',
                        padding: '4px',
                    }}
                >
                    <Option value="Bezugstermin">Bezugstermin</Option>
                    <Option value="Geruesttermin">Gerüsttermin</Option>
                    <Option value="Vormontage (A)">Vormontage (A)</Option>
                    <Option value="Vormontage (I)">Vormontage (I)</Option>
                    <Option value="Nicht Relevant">Nicht Relevant</Option>
                </Select>
            ),
        },
        {
            title: (
                <div>
                    Montage
                    <Select
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        onClick={e => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={val => handleFilterChange(val, 'assemblyType')}
                        style={{ width: '100%' }}
                    >
                        <Option value="Eigene Montage">Eigene Montage</Option>
                        <Option value="Fremdmontage">Fremdmontage</Option>
                        <Option value="Nur Lieferung">Nur Lieferung</Option>
                    </Select>
                </div>
            ),
            dataIndex: 'assemblyType',
            key: 'assemblyType',
            render: (value, record) => (
                <Select
                    value={record.assemblyType || "Eigene Montage"}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    onChange={val => handleDropdownChange(val, 'assemblyType', record.id)}
                    style={{
                        borderColor: isCellChanged(record, 'assemblyType') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                        borderRadius: isCellChanged(record, 'assemblyType') ? '7px' : '0px',
                        borderStyle: isCellChanged(record, 'assemblyType') ? 'groove' : 'none',
                        padding: '4px',
                    }}
                >
                    <Option value="Eigene Montage">Eigene Montage</Option>
                    <Option value="Fremdmontage">Fremdmontage</Option>
                    <Option value="Nur Lieferung">Nur Lieferung</Option>
                </Select>
            ),
        },
        {
            title: (
                <div>
                    Termin
                    {/* Optionaler Filter als WeekPicker */}
                    <DatePicker
                        picker="week"
                        allowClear
                        locale={locale}
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%', marginTop: 4 }}
                        format={(val) => {
                            const s = val.startOf('isoWeek');
                            return `${s.isoWeekYear()}/${String(s.isoWeek()).padStart(2,'0')}`;
                        }}
                        onChange={(val) => handleFilterChange(val ? dayjsToWeekString(val) : '', 'mountingDate')}
                        disabledDate={disablePastWeeks}
                    />
                </div>
            ),
            dataIndex: 'mountingDate',
            key: 'mountingDate',
            width: 120,
            render: (value: string | undefined, record: OrderCardData) => {
                const isLocked =
                    record.status === 'Montiert' || record.assemblyDone === '1'; // beide Fälle absichern
                return (
                    <div
                        style={{
                            padding: '4px',
                            borderColor: isCellChanged(record, 'mountingDate') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                            borderRadius: isCellChanged(record, 'mountingDate') ? '7px' : '0px',
                            borderStyle: isCellChanged(record, 'mountingDate') ? 'groove' : 'none',
                        }}
                    >
                        <DatePicker
                            picker="week"
                            locale={locale}
                            allowClear
                            format={(val) => {
                                const s = val.startOf('isoWeek');
                                return `${s.isoWeekYear()}/${String(s.isoWeek()).padStart(2,'0')}`;
                            }}
                            value={weekStringToDayjs(value)}
                            // --- NEW: statt sofort zu schreiben -> Modal öffnen
                            onChange={(val) => openDateChangeModal(val, record)}
                            disabled={isLocked}
                            disabledDate={disablePastWeeks}
                            style={{ width: '100%' }}
                        />
                    </div>
                );
            }
        },
        {
            title: (
                <Tooltip title="Zeichnen nach Theorie">
                    <span>ZnT</span>
                </Tooltip>
            ),
            dataIndex: 'drawByTheory',
            className: 'align-top',
            key: 'drawByTheory',
            width: 60,
            render: (value, record) =>
                renderCell(value, record, 'drawByTheory', val => (
                    <Checkbox
                        checked={val}
                        onChange={e => handleCheckboxChange(e.target.checked, record, 'drawByTheory')}
                    />
                )),
        },
        {
            title: 'Mangel',
            dataIndex: 'defectCard',
            className: 'align-top',
            key: 'defectCard',
            width: 60,
            render: (value, record) =>
                renderCell(value, record, 'defectCard', val => (
                    <Checkbox
                        checked={val}
                        onChange={e => handleCheckboxChange(e.target.checked, record, 'defectCard')}
                    />
                )),
        },
        {
            title: 'Regie',
            dataIndex: 'regieCard',
            className: 'align-top',
            key: 'regieCard',
            width: 60,
            render: (value, record) =>
                renderCell(value, record, 'regieCard', val => (
                    <Checkbox
                        checked={val}
                        onChange={e => handleCheckboxChange(e.target.checked, record, 'regieCard')}
                    />
                )),
        },
        {
            title: (
                <Tooltip title="Nicht relevant (wird im Netzplan nicht berücksichtigt)">
                    <span>NR</span>
                </Tooltip>
            ),
            dataIndex: 'notRelevant',
            className: 'align-top',
            key: 'notRelevant',
            width: 60,
            render: (value, record) =>
                renderCell(value, record, 'notRelevant', val => (
                    <Checkbox
                        checked={val}
                        onChange={e => handleCheckboxChange(e.target.checked, record, 'notRelevant')}
                    />
                )),
        },
        {
            title: (
                <div>
                    Lieferant
                    <Select
                        allowClear
                        onClick={e => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={val => handleFilterChange(val, 'supplierType')}
                        style={{ width: '100%' }}
                    >
                        <Option value="Gukom">Gukom</Option>
                        <Option value="Werkstatt">Werkstatt</Option>
                        <Option value="Dritte">Dritte</Option>
                    </Select>
                </div>
            ),
            dataIndex: 'supplierType',
            key: 'supplierType',
            render: (value, record) => (
                <Select
                    value={value}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    onChange={val => handleDropdownChange(val, 'supplierType', record.id)}
                    style={{
                        borderColor: isCellChanged(record, 'supplierType') ? 'rgb(159 206 116 / 66%)' : 'transparent',
                        borderRadius: isCellChanged(record, 'supplierType') ? '7px' : '0px',
                        borderStyle: isCellChanged(record, 'supplierType') ? 'groove' : 'none',
                        padding: '4px',
                    }}
                >
                    <Option value="Gukom">Gukom</Option>
                    <Option value="Werkstatt">Werkstatt</Option>
                    <Option value="Dritte">Dritte</Option>
                </Select>
            ),
            width: 130,
        },
        {
            title: 'DLZ (AT)',
            className: 'align-top',
            dataIndex: 'leapTime',
            key: 'leapTime',
            width: 70,
            render: (value, record) => (
                <DLZInputCell value={value} record={record} />
            )
        },
        {
            title: '',
            key: 'document',
            width: 100,
            render: (_, record) => {
                const hasDocuments = record.signedPlan
                return (
                    <><Tooltip title={hasDocuments ? "Dokumente anzeigen" : "Keine Dokumente vorhanden"}>
                        <Button
                            icon={<FileOutlined/>}
                            disabled={!hasDocuments}
                            onClick={() => openDocumentModal(record)}/>
                    </Tooltip><Tooltip title={"Unterschriebenen Ausführungsplan hochladen"}>
                        <Button
                            icon={<FileAddOutlined/>}
                            disabled={!!hasDocuments}
                            onClick={() => openDocumentUploadModal(record)}/>
                    </Tooltip></>
                );
            },
        },
    ];

    return (
        <div className="order-card-config" style={{padding: '10px'}}>
            <Space
                style={{
                    marginBottom: 16,
                    width: '100%',
                    justifyContent: 'flex-end', // Aligns items to the right
                    display: 'flex', // Ensures the flexbox behavior applies
                }}
            >
                <Button
                    icon={<UndoOutlined/>}
                    onClick={handleReset}
                    style={{marginRight: 8}}
                >
                    Daten neu laden
                </Button>
                {viewType !== ViewType.NEW_EXTERNAL && (
                    <Button
                        type="primary"
                        onClick={() => {
                            setNewOrderCard({
                                supplement: '',
                                protocol: '',
                                description: '',
                                estimatedValue: '',
                                status: '1',
                                jobCodeId: '',
                                mountingDate: undefined,
                                oglCodeId: '',
                                insideOutside: '',
                                relevantDeadline: '',
                                assemblyType: 'Eigene Montage',
                                defectCard: false,
                                regieCard: false,
                                supplierType: '',
                                leapTime: 1,
                            });
                            setIsModalVisible(true);
                        }}
                    >
                        Neue Auftragskarte erstellen
                    </Button>
                )}

                {viewType !== ViewType.NEW_EXTERNAL && (
                    <Button
                        type="primary"
                        icon={<SaveOutlined/>}
                        disabled={!hasChanges()}
                        onClick={handleSave}
                    >
                        Speichern
                    </Button>
                )}
            </Space>
            <Table
                columns={columns}
                dataSource={data}
                scroll={{y: 700}}
                rowKey="key"
                pagination={{pageSize: 100}}
                rowClassName={(record) => record.assemblyDone === "1" ? "gray-out-row" : ""}
                loading={isLoading}
            />

            <Modal
                title="Verlinkte Helios-Dokumente"
                open={isDocumentModalVisible}
                footer={
                    <Button type="primary" onClick={closeDocumentModal}>
                        Schliessen
                    </Button>
                }
                onCancel={closeDocumentModal}
            >
                {selectedRecord && (
                    <table style={{width: '100%', borderCollapse: 'collapse'}}>
                        <thead>
                        <tr>
                            <th style={{textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd'}}>Name</th>
                            <th style={{textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd'}}>Wert</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Object.entries(selectedRecord)
                            .filter(([key, value]) =>
                                [
                                    'signedPlan'
                                ].includes(key) && value
                            )
                            .map(([key, value]) => {
                                const mapping: Record<string, string> = {
                                    signedPlan: 'Unterschriebener Ausführungsplan'
                                };
                                return (
                                    <tr key={key}>
                                        <td style={{padding: '8px', borderBottom: '1px solid #ddd'}}>
                                            <strong>{mapping[key]}</strong>
                                        </td>
                                        <td style={{padding: '8px', borderBottom: '1px solid #ddd'}}>
                                            {key === 'systemPlanningApprovalDate' ? (
                                                value
                                            ) : (
                                                <a href={value as string} target="_blank" rel="noopener noreferrer">
                                                    {value}
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </Modal>
            <Modal
                title="Unterschriebenen Ausführungsplan hochladen"
                open={isDocumentUploadModalVisible}
                onOk={handleDocumentUploadModalOk}
                onCancel={closeDocumentUploadModal}
                footer={[
                    <Button
                        key="cancel"
                        onClick={closeDocumentUploadModal}
                        style={{backgroundColor: 'red', color: 'white'}}
                    >
                        Abbrechen
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        disabled={!heliosSystemPlansUrl}
                        loading={isSystemPlanSaving}
                        onClick={handleDocumentUploadModalOk}
                    >
                        Speichern
                    </Button>,
                ]}
            >
                <Input
                    placeholder="Helios-URL"
                    value={heliosSystemPlansUrl}
                    onChange={(e) => handleHeliosLinkInputChange(e.target.value, setHeliosSystemPlansUrl)}
                    style={{marginBottom: '15px'}}
                />
            </Modal>
            <Modal
                title="Neue Auftragskarte erstellen"
                open={isModalVisible}
                onOk={handleSaveNewOrderCard}
                onCancel={() => setIsModalVisible(false)}
                okText="Speichern"
                cancelText="Abbrechen"
                confirmLoading={isSavingNewCard}
                okButtonProps={{disabled: !requiredFieldsFilled}}
            >
                <label>Nachtrag</label>
                <Select
                    placeholder="Supplement wählen"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    value={newOrderCard.supplement}
                    onChange={(val) => setNewOrderCard({...newOrderCard, supplement: val})}
                    style={{width: '100%', marginBottom: 12}}
                >
                    {supplements.map((supplement) => (
                        <Option key={supplement} value={supplement}>
                            {supplement}
                        </Option>
                    ))}
                </Select>
                <label>Bauabschnitt</label>
                <Select
                    placeholder="Bauabschnitt"
                    value={newOrderCard.oglCodeId}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    onChange={val => {
                        setNewOrderCard(prev => ({
                            ...prev,
                            oglCodeId: val,
                            description: isDescriptionEdited
                                ? prev.description
                                : generateDescription(val ?? "", prev.jobCodeId ?? ""),
                        }));
                    }}
                    style={{width: '100%', marginBottom: 12}}
                >
                    {localOGLCodes.map(code => (
                        <Option key={code.ogl} value={code.ogl}>
                            {code.description}
                        </Option>
                    ))}
                </Select>
                <label>Produkt</label>
                <Select
                    placeholder="Produkt"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    value={newOrderCard.jobCodeId}
                    onChange={val => {
                        setNewOrderCard(prev => ({
                            ...prev,
                            jobCodeId: val,
                            description: isDescriptionEdited
                                ? prev.description
                                : generateDescription(prev.oglCodeId ?? "", val ?? ""),
                        }));
                    }}
                    style={{width: '100%', marginBottom: 12}}
                >
                    {localJobCodes.map(code => (
                        <Option key={code.alias} value={code.alias}>
                            {code.descriptionSpez}
                        </Option>
                    ))}
                </Select>
                <label style={{marginBottom: 4}}>Name</label>
                <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 12}}>
                    <Input
                        value={newOrderCard.description}
                        disabled={!isDescriptionEdited}
                        onChange={e => setNewOrderCard({...newOrderCard, description: e.target.value})}
                        style={{flex: 1}}
                    />
                    <Tooltip title={isDescriptionEdited ? "Bearbeitung deaktivieren" : "Bearbeiten"}>
                        <Button
                            icon={<EditOutlined/>}
                            size="small"
                            onClick={() => setIsDescriptionEdited(!isDescriptionEdited)}
                        />
                    </Tooltip>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12}}>
                    <label>Typ</label>
                    <Checkbox
                        checked={newOrderCard.regieCard}
                        onChange={e => {
                            const checked = e.target.checked;
                            setNewOrderCard({
                                ...newOrderCard,
                                regieCard: checked,
                                defectCard: checked ? false : newOrderCard.defectCard,
                                defectCardReason: checked ? '' : newOrderCard.defectCardReason,
                            });
                        }}
                    >
                        Regie
                    </Checkbox>
                    <Checkbox
                        checked={newOrderCard.defectCard}
                        onChange={e => {
                            const checked = e.target.checked;
                            setNewOrderCard({
                                ...newOrderCard,
                                defectCard: checked,
                                regieCard: checked ? false : newOrderCard.regieCard,
                                defectCardReason: checked ? '' : '', // reset reason if unselected
                            });
                        }}
                    >
                        Mangel
                    </Checkbox>
                    <label>Einstiegspunkt</label>
                    <Select
                        placeholder="Einstiegspunkt"
                        showSearch
                        allowClear
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                        }
                        value={newOrderCard.defectCardReason}
                        onChange={val =>
                            setNewOrderCard({...newOrderCard, defectCardReason: val})
                        }
                        style={{width: '100%'}}
                    >
                        {defectReasons.map(reason => (
                            <Option key={reason.code} value={reason.code}>
                                {reason.label}
                            </Option>
                        ))}
                    </Select>

                </div>
                <label>Montage-Datum</label>
                <DatePicker
                    locale={locale}
                    style={{width: '100%', marginBottom: 12}}
                    format="DD.MM.YYYY"
                    value={newOrderCard.mountingDate ? dayjs(newOrderCard.mountingDate, "DD.MM.YYYY") : undefined}
                    onChange={(date) =>
                        setNewOrderCard({
                            ...newOrderCard,
                            mountingDate: date ? date.format("DD.MM.YYYY") : undefined,
                        })
                    }
                />
                <label>Status</label>
                <Select
                    placeholder="Status"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    value={newOrderCard.status}
                    onChange={val => setNewOrderCard({...newOrderCard, status: val})}
                    style={{width: '100%', marginBottom: 12}}
                >
                    <Option value="1">Messen</Option>
                    <Option value="2">Zeichnen</Option>
                    <Option value="3">Auszüge</Option>
                    <Option value="4">Kontrolle</Option>
                    <Option value="5">Kon.fertig</Option>
                </Select>
                <label>Einbauort</label>
                <Select
                    placeholder="Einbauort"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    value={newOrderCard.insideOutside}
                    onChange={val => setNewOrderCard({...newOrderCard, insideOutside: val})}
                    style={{width: '100%', marginBottom: 12}}
                >
                    <Option value="Aussen">Aussen</Option>
                    <Option value="Innen">Innen</Option>
                    <Option value="Umgebung">Umgebung</Option>
                    <Option value="Nicht Relevant">Nicht Relevant</Option>
                </Select>
                <label>Relevante Frist</label>
                <Select
                    placeholder="Relevante Frist"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    value={newOrderCard.relevantDeadline}
                    onChange={val => setNewOrderCard({...newOrderCard, relevantDeadline: val})}
                    style={{width: '100%', marginBottom: 12}}
                >
                    <Option value="Bezugstermin">Bezugstermin</Option>
                    <Option value="Geruesttermin">Gerüsttermin</Option>
                    <Option value="Vormontage (A)">Vormontage (A)</Option>
                    <Option value="Vormontage (I)">Vormontage (I)</Option>
                    <Option value="Nicht Relevant">Nicht Relevant</Option>
                </Select>
                <label>Montage</label>
                <Select
                    placeholder="Montage"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    value={newOrderCard.assemblyType}
                    onChange={val => setNewOrderCard({...newOrderCard, assemblyType: val})}
                    style={{width: '100%', marginBottom: 12}}
                >
                    <Option value="Eigene Montage">Eigene Montage</Option>
                    <Option value="Fremdmontage">Fremdmontage</Option>
                    <Option value="Nur Lieferung">Nur Lieferung</Option>
                </Select>
                <label>Lieferant</label>
                <Select
                    placeholder="Lieferant"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                        (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                    }
                    value={newOrderCard.supplierType}
                    onChange={val => {
                        const lt = val === 'Dritte' ? 1 : (val === 'Gukom' ? 35 : 10);
                        setNewOrderCard({...newOrderCard, supplierType: val, leapTime: lt});
                    }}
                    style={{width: '100%', marginBottom: 12}}
                >
                    <Option value="Gukom">Gukom</Option>
                    <Option value="Werkstatt">Werkstatt</Option>
                    <Option value="Dritte">Dritte</Option>
                </Select>
                <label>DLZ (Tage)</label>
                <Input
                    placeholder="DLZ (AT)"
                    type="number"
                    value={newOrderCard.leapTime?.toString() ?? ''}
                    disabled={newOrderCard.supplierType !== 'Dritte'}
                    onChange={e => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) {
                            setNewOrderCard({...newOrderCard, leapTime: val === '' ? undefined : parseInt(val, 10)});
                        }
                    }}
                    onBlur={e => {
                        const num = parseInt(e.target.value, 10);
                        if (isNaN(num) || num < 1) {
                            setNewOrderCard({...newOrderCard, leapTime: 1}); // fallback auf 1
                        }
                    }}
                />
            </Modal>

            {/* NEW: Termin-Änderung mit Pflicht-Begründung */}
            <Modal
                title="Termin ändern – Begründung erforderlich"
                open={isDateChangeModalVisible}
                onCancel={cancelDateChange}
                footer={[
                    <Button key="cancel" onClick={cancelDateChange}>
                        Abbrechen
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={confirmDateChange}
                        disabled={!pendingDelayReason.trim()}
                    >
                        Speichern
                    </Button>,
                ]}
            >
                <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                    <div>
                        <label style={{display: 'block', marginBottom: 6}}>Kalenderwoche</label>
                        <DatePicker
                            picker="week"
                            locale={locale}
                            allowClear
                            style={{width: '100%'}}
                            disabledDate={disablePastWeeks}
                            value={pendingDateChange?.newWeek ?? null}
                            format={(val) => {
                                const s = val.startOf('isoWeek');
                                return `${s.isoWeekYear()}/${String(s.isoWeek()).padStart(2, '0')}`;
                            }}
                            onChange={(val) => {
                                if (!pendingDateChange) return;
                                setPendingDateChange({...pendingDateChange, newWeek: val ?? null});
                            }}
                        />
                    </div>
                    <div>
                        <label style={{display: 'block', marginBottom: 6}}>Begründung (Pflicht)</label>
                        <Select
                            key={`reason-sel-${reasonSelectResetKey}`}
                            placeholder="Vorlage oder zuletzt verwendete Begründung auswählen"
                            showSearch
                            allowClear
                            style={{ width: '100%', marginBottom: 8 }}
                            value={undefined} // immer Placeholder anzeigen
                            onDropdownVisibleChange={(open) => {
                                if (open) {
                                    // Nur Textarea leeren, KEIN setReasonSelectResetKey hier!
                                    setPendingDelayReason('');
                                }
                            }}
                            onChange={(val) => {
                                if (!val) return;
                                setPendingDelayReason(String(val));
                            }}
                            dropdownMatchSelectWidth
                        >
                            {recentDelayReasons.length > 0 && (
                                <Select.OptGroup label="Zuletzt verwendet">
                                    {recentDelayReasons.map((r, i) => (
                                        <Option key={`recent-${i}`} value={r}>{r}</Option>
                                    ))}
                                </Select.OptGroup>
                            )}
                            <Select.OptGroup label="Vorlagen">
                                {TEMPLATES.map((t, i) => (
                                    <Option key={`tpl-${i}`} value={t}>{t}</Option>
                                ))}
                            </Select.OptGroup>

                        </Select>

                        <TextArea
                            autoSize={{minRows: 3}}
                            placeholder="Bitte die Begründung für die Terminänderung eingeben…"
                            value={pendingDelayReason}
                            onChange={(e) => setPendingDelayReason(e.target.value)}
                            status={!pendingDelayReason.trim() ? 'error' : ''}
                            // --- NEW: schon beim Verlassen zwischenspeichern (ohne Persistenz)
                            onBlur={() => rememberReason(pendingDelayReason)}
                        />
                    </div>
                </div>
            </Modal>
        </div>

    );
};

export default OrderCardConfiguration;